import { ReactNode } from "react";

import { LinkButton } from "@/ui/next/LinkButton";
import Link from "@whoppah/next/link";
import { Trans } from "next-i18next";

import { SellerAccountStatus } from "@/Apollo/schema";
import { IconStateAlarmClock } from "@/assets/icons/figma/IconStateAlarmClock";
import { IconStateCheckmarkCircleOutline } from "@/assets/icons/figma/IconStateCheckmarkCircleOutline";
import { IconStateInformation } from "@/assets/icons/figma/IconStateInformation";
import { IconStateWarningAlert } from "@/assets/icons/figma/IconStateWarningAlert";
import { CONTACT_US_ROUTE } from "@/common/static/routes.static";
import { cn } from "@/common/utils/css.utils";
import {
  STATUS_PRESENTATION,
  StatusTone,
} from "@/components/Account/payment/payment.static";
import {
  PrimaryAction,
  SecondaryAction,
  StripeLinkType,
} from "@/components/Account/payment/payment.utils";
import { usePaymentCopy } from "@/components/Account/payment/usePaymentCopy";
import { InfoPanel } from "@/ui/InfoPanel";
import { Button } from "@/ui/cva/Button";
import { Heading } from "@/ui/cva/Heading";
import { InfoLabel } from "@/ui/cva/InfoLabel";
import { Body } from "@/ui/typography/Body";

/** Card tint per tone, the 50 shades the InfoPanel already uses. Static so Tailwind keeps them. */
const CARD_TONE_CLASS: Record<StatusTone, string> = {
  grey: "border-grey-200 bg-grey-50",
  brand: "border-brand-200 bg-flavour-50",
  burst: "border-burst-200 bg-burst-50",
  attention: "border-attention-200 bg-attention-50",
  curious: "border-curious-200 bg-curious-50",
  dark: "border-grey-300 bg-grey-100",
};

const PILL_COLOR: Record<
  StatusTone,
  "grey" | "brand" | "burst" | "attention" | "curious"
> = {
  grey: "grey",
  brand: "brand",
  burst: "burst",
  attention: "attention",
  curious: "curious",
  dark: "grey",
};

/** "Being checked" wears a clock, "Action needed" an exclamation mark: the two must never look alike. */
const PILL_ICON: Partial<
  Record<SellerAccountStatus, (props: { className?: string }) => JSX.Element>
> = {
  [SellerAccountStatus.UnderReview]: IconStateAlarmClock,
  [SellerAccountStatus.BlockedActionNeeded]: IconStateWarningAlert,
  [SellerAccountStatus.AccountNotCreated]: IconStateWarningAlert,
  [SellerAccountStatus.ActiveActionNeeded]: IconStateInformation,
  [SellerAccountStatus.Ready]: IconStateCheckmarkCircleOutline,
};

export type DeadlinePanel = { date: string; urgent: boolean };

/**
 * Shown only when the gateway could not re-read Stripe on open (the mirror
 * is older than its refresh window): how old the status is, and a way to
 * try again. A fresh status says nothing about its age.
 */
export type StaleStatus = {
  label: string;
  onCheckAgain: () => void;
  checking: boolean;
};

export type StatusCardProps = {
  status: SellerAccountStatus;
  title: string;
  description: string;
  stale: StaleStatus | null;
  amountLine: string | null;
  deadline: DeadlinePanel | null;
  /** Review running long: show the panel; `contact` adds the customer-service link. */
  slowReview: { contact: boolean } | null;
  primary: PrimaryAction;
  secondary: SecondaryAction;
  onStripeLink: (linkType: StripeLinkType) => void;
  redirecting: boolean;
};

const ICON_CLASS = "size-6 shrink-0";

export const StatusCard = ({
  status,
  title,
  description,
  stale,
  amountLine,
  deadline,
  slowReview,
  primary,
  secondary,
  onStripeLink,
  redirecting,
}: StatusCardProps) => {
  const { copy, key, defaults } = usePaymentCopy();
  const presentation = STATUS_PRESENTATION[status];
  const Icon = PILL_ICON[status];

  const renderPrimary = (): ReactNode => {
    if (primary.kind === "stripe-link") {
      return (
        <>
          <Button
            type="button"
            color={primary.color}
            size="large"
            className="w-full md:w-max md:min-w-64"
            loading={redirecting}
            disabled={primary.disabled}
            onClick={() => onStripeLink(primary.linkType)}
          >
            {primary.label ?? copy(primary.labelKey)}
          </Button>
          {primary.disabled && (
            <InfoPanel
              color="curious"
              icon={<IconStateInformation className={ICON_CLASS} />}
              body={<p>{primary.disabledReason ?? copy("noListingYet")}</p>}
            />
          )}
        </>
      );
    }

    if (primary.kind === "route") {
      return (
        <LinkButton
          href={primary.href}
          color={primary.color}
          size="large"
          className="w-full md:w-max md:min-w-64"
        >
          {primary.label ?? copy(primary.labelKey)}
        </LinkButton>
      );
    }

    return null;
  };

  const renderSecondary = (): ReactNode => {
    if (!secondary) {
      return null;
    }

    if (secondary.kind === "stripe-link") {
      return (
        <Button
          type="button"
          appearance={secondary.appearance}
          size="large"
          className="md:w-max md:min-w-64"
          loading={redirecting}
          onClick={() => onStripeLink(secondary.linkType)}
        >
          {secondary.label ?? copy(secondary.labelKey)}
        </Button>
      );
    }

    return (
      <LinkButton
        href={secondary.href}
        appearance={secondary.appearance}
        size="large"
        className="md:w-max md:min-w-64"
      >
        {secondary.label ?? copy(secondary.labelKey)}
      </LinkButton>
    );
  };

  return (
    <section
      aria-labelledby="payment-status-title"
      data-status={status}
      className={cn(
        "flex flex-col gap-4 rounded-[0.25rem] border p-4 md:p-6",
        CARD_TONE_CLASS[presentation.tone]
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <InfoLabel
          color={PILL_COLOR[presentation.tone]}
          variant={presentation.tone === "dark" ? "dark" : "light"}
          corners="rounded"
          weight="bold"
        >
          <>
            {Icon && <Icon className="size-4" />}
            <span>{copy(presentation.pill)}</span>
          </>
        </InfoLabel>
      </div>
      <Heading as="h2" size="h3" id="payment-status-title">
        {title}
      </Heading>
      {amountLine && (
        <Body as="p" className="text-xl font-bold">
          {amountLine}
        </Body>
      )}
      <Body as="p">{description}</Body>
      {deadline && (
        <InfoPanel
          color={deadline.urgent ? "attention" : "burst"}
          icon={<IconStateAlarmClock className={ICON_CLASS} />}
          body={<p>{copy("deadlineBefore", { date: deadline.date })}</p>}
        />
      )}
      {slowReview && (
        <InfoPanel
          color="curious"
          icon={<IconStateAlarmClock className={ICON_CLASS} />}
          body={
            <p>
              {copy("reviewSlow")}
              {slowReview.contact && (
                <>
                  {" "}
                  <Trans
                    i18nKey={key("reviewSlowContact")}
                    defaults={defaults("reviewSlowContact")}
                    components={{
                      support: (
                        <Link href={CONTACT_US_ROUTE} className="underline" />
                      ),
                    }}
                  />
                </>
              )}
            </p>
          }
        />
      )}
      {(primary.kind !== "none" && primary.kind !== "bank-form") ||
      secondary ? (
        <div className="flex flex-col gap-3 pt-2">
          {renderPrimary()}
          {renderSecondary()}
        </div>
      ) : null}
      {stale && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-black/10 pt-3">
          <Body as="span" color="muted" className="text-sm">
            {stale.label}
          </Body>
          <Button
            type="button"
            appearance="minimal"
            size="small"
            loading={stale.checking}
            onClick={stale.onCheckAgain}
          >
            {copy("checkAgain")}
          </Button>
        </div>
      )}
    </section>
  );
};

/** First paint of a plain open: the card's shape, no words, until the status arrives. */
export const StatusCardSkeleton = () => (
  <section
    aria-busy="true"
    data-status="LOADING"
    className="flex animate-pulse flex-col gap-4 rounded-[0.25rem] border border-grey-200 bg-grey-50 p-4 md:p-6"
  >
    <span className="h-6 w-32 rounded bg-grey-200" />
    <span className="h-8 w-3/4 rounded bg-grey-200" />
    <span className="h-4 w-full rounded bg-grey-200" />
    <span className="h-4 w-5/6 rounded bg-grey-200" />
    <span className="mt-2 h-12 w-full rounded bg-grey-200 md:w-64" />
  </section>
);

/** The client-side waiting state while the status is re-read after Stripe. */
export const FetchingStatusCard = () => {
  const { copy } = usePaymentCopy();

  return (
    <section
      aria-live="polite"
      data-status="FETCHING"
      className={cn(
        "flex flex-col gap-4 rounded-[0.25rem] border p-4 md:p-6",
        CARD_TONE_CLASS.curious
      )}
    >
      <InfoLabel color="curious" corners="rounded" weight="bold">
        <>
          <IconStateAlarmClock className="size-4 animate-pulse" />
          <span>{copy("pillFetching")}</span>
        </>
      </InfoLabel>
      <Heading as="h2" size="h3">
        {copy("fetchingTitle")}
      </Heading>
      <Body as="p">{copy("fetchingBody")}</Body>
    </section>
  );
};
